import { useState } from 'react';

import { AppShell } from '@/components/layout/AppShell';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Label,
  Badge,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Alert,
  KpiCard,
  PageContainer,
  PageHeader,
  EmptyState,
} from '@/components/ui';
import { SearchIcon, BellIcon, ChevronRightIcon } from '@/components/icons';

/**
 * Página interna de desenvolvimento — showcase de todos os componentes UI.
 *
 * Acessível em /dev/ui. Útil pra:
 *   - Conferir visual de cada componente
 *   - Testar variants e tamanhos
 *   - Validar modo claro/escuro
 *   - Servir de referência pra implementar telas novas
 *
 * Não aparece na navegação principal. Acesso direto pela URL.
 */
export function UiShowcase() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <AppShell>
      {/* LAYOUT-CANONICO: a própria vitrine seguia a régua no braço. */}
      <PageContainer>
        <header className="mb-10">
          <p className="eyebrow mb-2">Desenvolvimento · Design System</p>
          <h1 className="font-display text-5xl md:text-6xl text-ink-1 dark:text-ink-dark-1 tracking-tighter mb-3">
            Componentes UI
          </h1>
          <p className="text-ink-2 dark:text-ink-dark-2 leading-relaxed max-w-2xl">
            Galeria interna de todos os componentes reutilizáveis do design system.
            Use como referência ao construir telas novas.
          </p>
        </header>

        {/* BUTTONS */}
        <Section title="Buttons" description="Variants + tamanhos">
          <div className="space-y-4">
            <Row>
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="link">Link →</Button>
            </Row>
            <Row>
              <Button size="sm">Small</Button>
              <Button size="md">Medium (default)</Button>
              <Button size="lg">Large</Button>
            </Row>
            <Row>
              <Button loading>Salvando…</Button>
              <Button disabled>Desabilitado</Button>
              <Button leftIcon={<SearchIcon className="w-4 h-4" />}>Com ícone</Button>
              <Button rightIcon={<ChevronRightIcon className="w-4 h-4" />}>Avançar</Button>
            </Row>
            <Row>
              <Button fullWidth>Full width</Button>
            </Row>
          </div>
        </Section>

        {/* BADGES */}
        <Section title="Badges" description="Status, contagens, tags">
          <div className="space-y-3">
            <Row>
              <Badge variant="success" dot>Ganho</Badge>
              <Badge variant="warning" dot>Em aberto</Badge>
              <Badge variant="danger" dot>Urgente</Badge>
              <Badge variant="neutral" dot>Rascunho</Badge>
              <Badge variant="info" dot>Novo</Badge>
            </Row>
            <Row>
              <Badge variant="success">12</Badge>
              <Badge variant="danger" size="sm">3 atrasados</Badge>
              <Badge variant="neutral">Sem categoria</Badge>
            </Row>
          </div>
        </Section>

        {/* FASE-B: cabeçalho canônico de página (h1 obrigatório em toda tela) */}
        <Section title="PageHeader" description="Crumb + h1 Oswald + subtítulo + ações">
          <div className="rounded-xl border border-line dark:border-line-dark p-6">
            <PageHeader
              crumb="Comercial"
              title="Funil de negócios"
              subtitle="8 propostas ativas · R$ 31,2 mil em negociação"
              actions={<Button>Nova proposta</Button>}
              className="mb-0"
            />
          </div>
        </Section>

        {/* FASE-B: KPI com slots e altura fixos — fileira sempre alinhada */}
        <Section title="KpiCard" description="Label 12 caps · valor SF Mono 24 · contexto; altura fixa na fileira">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard label="Propostas ganhas" value="12" context="6 abertas no funil" />
            <KpiCard label="Mensalidades novas" value="R$ 1.500" valueSuffix="/mês" context="meta: R$ 4.000" />
            <KpiCard label="Taxa de conversão" value="18%" context="12 de 66 propostas" />
            <KpiCard
              label="Faturamento pontual"
              value="R$ 0"
              context={
                <>
                  sem meta · <a href="/admin/comercial/metas">definir em Metas</a>
                </>
              }
            />
          </div>
        </Section>

        {/* FASE-B: estado vazio canônico */}
        <Section title="EmptyState" description="Ícone + frase humana + ação — nunca tela em branco">
          <div className="rounded-xl border border-line dark:border-line-dark">
            <EmptyState
              icon={<SearchIcon className="w-5 h-5" />}
              title="Nenhuma proposta em qualificação"
              description="Arraste um card pra cá ou crie uma proposta nova."
              action={<Button variant="outline">Nova proposta</Button>}
            />
          </div>
        </Section>

        {/* INPUTS */}
        <Section title="Inputs" description="Campos de formulário">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            <div>
              <Label htmlFor="i1" required>Email</Label>
              <Input id="i1" type="email" placeholder="seu@email.com" />
            </div>
            <div>
              <Label htmlFor="i2">Senha</Label>
              <Input
                id="i2"
                type="password"
                placeholder="••••••••"
                hint="Mínimo 8 caracteres"
              />
            </div>
            <div>
              <Label htmlFor="i3">Email com erro</Label>
              <Input
                id="i3"
                type="email"
                defaultValue="invalido"
                error="Email inválido"
              />
            </div>
            <div>
              <Label htmlFor="i4">Buscar</Label>
              <Input
                id="i4"
                placeholder="Cliente, OS, deal..."
                leftIcon={<SearchIcon className="w-4 h-4" />}
              />
            </div>
            <div>
              <Label htmlFor="s1">Departamento</Label>
              <Select id="s1" defaultValue="">
                <option value="" disabled>Selecione...</option>
                <option value="comercial">Comercial</option>
                <option value="manutencao">Manutenção</option>
                <option value="financeiro">Financeiro</option>
              </Select>
            </div>
            <div>
              <Label>Desabilitado</Label>
              <Input disabled defaultValue="Não editável" />
            </div>
          </div>
        </Section>

        {/* CARDS */}
        <Section title="Cards" description="Containers flutuantes">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Negócios em aberto</CardTitle>
                <CardDescription>15 deals somando R$ 487 mil</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                  Três deles vencem essa semana. Considere priorizar.
                </p>
              </CardContent>
              <CardFooter>
                <Button size="sm" variant="ghost">Ver fluxo</Button>
              </CardFooter>
            </Card>

            <Card variant="urgent">
              <CardHeader>
                <CardTitle>Monterrey</CardTitle>
                <CardDescription>12 dias sem retorno</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                  Última proposta: R$ 15.300 para central de comando.
                </p>
              </CardContent>
            </Card>

            <Card variant="flat">
              <CardHeader>
                <CardTitle>Card plano</CardTitle>
                <CardDescription>Sem sombra, só borda</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                  Útil em listagens densas ou seções secundárias.
                </p>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* ALERTS */}
        <Section title="Alerts" description="Mensagens destacadas">
          <div className="space-y-3 max-w-2xl">
            <Alert variant="info" title="3 orçamentos sem resposta há mais de 7 dias">
              Recomendamos follow-up imediato com Monterrey, Viva Vista e Fortaleza.
            </Alert>
            <Alert variant="success" title="Deal fechado!">
              Saint Michael · R$ 24.800 · Portaria Remota
            </Alert>
            <Alert variant="warning" title="SLA vencendo em breve" rail>
              5 chamados precisam de resposta nas próximas 24h.
            </Alert>
            <Alert variant="danger" title="Backup falhou">
              Último backup automático falhou às 03:00. Verifique o cron.
            </Alert>
          </div>
        </Section>

        {/* TABS */}
        <Section title="Tabs" description="Navegação por abas">
          <Tabs defaultValue="todos">
            <TabsList>
              <TabsTrigger value="todos">Todos</TabsTrigger>
              <TabsTrigger value="abertos">Em aberto</TabsTrigger>
              <TabsTrigger value="fechados">Fechados</TabsTrigger>
              <TabsTrigger value="perdidos">Perdidos</TabsTrigger>
            </TabsList>
            <TabsContent value="todos">
              <p className="text-ink-2 dark:text-ink-dark-2">Conteúdo da aba &ldquo;Todos&rdquo; — 15 deals.</p>
            </TabsContent>
            <TabsContent value="abertos">
              <p className="text-ink-2 dark:text-ink-dark-2">Conteúdo da aba &ldquo;Em aberto&rdquo; — 10 deals.</p>
            </TabsContent>
            <TabsContent value="fechados">
              <p className="text-ink-2 dark:text-ink-dark-2">Conteúdo da aba &ldquo;Fechados&rdquo; — 4 deals.</p>
            </TabsContent>
            <TabsContent value="perdidos">
              <p className="text-ink-2 dark:text-ink-dark-2">Conteúdo da aba &ldquo;Perdidos&rdquo; — 1 deal.</p>
            </TabsContent>
          </Tabs>
        </Section>

        {/* TABLE */}
        <Section title="Table" description="Listagem tabular">
          <Card variant="flat" className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead align="right">Valor</TableHead>
                  <TableHead align="right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-display text-base">Saint Michael</TableCell>
                  <TableCell className="text-ink-2">Portaria Remota · 8 ramais</TableCell>
                  <TableCell align="right" className="font-display italic">R$ 24.800</TableCell>
                  <TableCell align="right"><Badge variant="warning" dot>Aberto</Badge></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-display text-base">Ponte Nova</TableCell>
                  <TableCell className="text-ink-2">CFTV · 16 câmeras</TableCell>
                  <TableCell align="right" className="font-display italic">R$ 18.200</TableCell>
                  <TableCell align="right"><Badge variant="success" dot>Ganho</Badge></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-display text-base">Monterrey</TableCell>
                  <TableCell className="text-ink-2">Central de Comando</TableCell>
                  <TableCell align="right" className="font-display italic">R$ 15.300</TableCell>
                  <TableCell align="right"><Badge variant="danger" dot>Urgente</Badge></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Section>

        {/* DIALOG */}
        <Section title="Dialog" description="Modal/Overlay">
          <Button onClick={() => setDialogOpen(true)}>Abrir Dialog</Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogHeader>
              <DialogTitle>Confirmar exclusão</DialogTitle>
              <DialogDescription>
                Tem certeza que quer excluir esse cliente? Essa ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogContent>
              <Alert variant="warning">
                Todos os deals, OS e contatos vinculados a esse cliente também serão removidos.
              </Alert>
            </DialogContent>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button variant="destructive">Excluir cliente</Button>
            </DialogFooter>
          </Dialog>
        </Section>

        {/* ICON LIBRARY */}
        <Section title="Iconography" description="Outline · stroke 1.8 · 24px">
          <div className="flex flex-wrap gap-3 text-ink-1 dark:text-ink-dark-1">
            <IconBox label="Search"><SearchIcon /></IconBox>
            <IconBox label="Bell"><BellIcon /></IconBox>
            <IconBox label="Chevron"><ChevronRightIcon /></IconBox>
          </div>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-3">
            Biblioteca completa em <code className="font-mono">@/components/icons</code> — 16 ícones.
          </p>
        </Section>

        {/* PAGE CONTAINER — 10º canônico */}
        <Section
          title="PageContainer"
          description="A régua de margem e largura de TODA tela. Duas variantes, e só duas."
        >
          <div className="space-y-4">
            <p className="text-sm text-ink-2 dark:text-ink-dark-2 max-w-3xl">
              Antes desta rodada cada tela inventava o próprio wrapper: em 1568px o conteúdo
              começava em 288px, 312px, 320px ou 448px, com larguras de 1280, 1200, 1024 ou 896.
              Lado a lado, Clientes, Funil e Ajuda não pareciam o mesmo sistema. Agora a margem
              vem daqui — <strong>padding inventado em tela é bug de review</strong>.
            </p>

            <div className="rounded-lg border border-line dark:border-line-dark overflow-hidden">
              <div className="bg-paper-warm dark:bg-paper-dark-warm px-3 py-2 text-xs font-semibold text-ink-2 dark:text-ink-dark-2">
                variante <code className="font-mono">padrao</code> — largura FLUIDA, ancorada à
                esquerda. É o default: use em toda tela de lista, ficha, formulário ou painel. O teto
                de 1440px saiu na rodada LARGURA-E-ESCALA (a 1860px sobravam ~450px de área morta).
              </div>
              <div className="bg-red-soft/40 dark:bg-red-soft-dark/40 p-2">
                <div className="w-full bg-paper-surface dark:bg-paper-dark-surface border border-dashed border-red/40 px-4 md:px-8 py-6 md:py-10 text-center text-xs text-ink-2 dark:text-ink-dark-2">
                  conteúdo · px-4 md:px-8 · py-6 md:py-10 · sem teto de largura
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-line dark:border-line-dark overflow-hidden">
              <div className="bg-paper-warm dark:bg-paper-dark-warm px-3 py-2 text-xs font-semibold text-ink-2 dark:text-ink-dark-2">
                variante <code className="font-mono">full</code> — ocupa toda a largura. Só para
                conteúdo que rola na horizontal (os Kanbans do Funil e dos Leads), com{' '}
                <code className="font-mono">alturaCheia</code> quando a rolagem for interna.
              </div>
              <div className="bg-red-soft/40 dark:bg-red-soft-dark/40 p-2">
                <div className="w-full bg-paper-surface dark:bg-paper-dark-surface border border-dashed border-red/40 px-4 md:px-8 py-6 md:py-10 text-center text-xs text-ink-2 dark:text-ink-dark-2">
                  conteúdo · mesmo padding · sem teto de largura
                </div>
              </div>
            </div>

            <p className="text-xs text-ink-3 dark:text-ink-dark-3">
              Não aceita <code className="font-mono">className</code> de propósito. Classe de
              layout interno (<code className="font-mono">space-y-*</code>,{' '}
              <code className="font-mono">flex</code>) vai num div dentro dele.
            </p>
          </div>
        </Section>
      </PageContainer>
    </AppShell>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <h2 className="font-display text-2xl text-ink-1 dark:text-ink-dark-1 tracking-tight mb-1">{title}</h2>
      {description && <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-5">{description}</p>}
      {children}
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>;
}

function IconBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-1 p-3 rounded-lg border border-line dark:border-line-dark min-w-[80px]">
      {children}
      <span className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">{label}</span>
    </div>
  );
}
